import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, ChevronDown } from "lucide-react";
import { filterNavItems, isNavGroupActive, navItems, socials } from "./nav-data";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";

export function MobileMenu() {
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Ticket ME1/KN1: verborgene Bereiche erscheinen in keinem Menü.
  const mentorings = useFeatureFlag(FEATURE_FLAGS.mentoringsVisible);
  const keynotes = useFeatureFlag(FEATURE_FLAGS.keynotesVisible);
  const items = filterNavItems(navItems, [
    ...(mentorings.enabled ? [FEATURE_FLAGS.mentoringsVisible] : []),
    ...(keynotes.enabled ? [FEATURE_FLAGS.keynotesVisible] : []),
  ]);

  const toggleGroup = (label: string) =>
    setOpenGroups((g) => ({ ...g, [label]: !g[label] }));

  return (
    <div className="lg:hidden">
      <button
        type="button"
        aria-label={open ? "Menü schließen" : "Menü öffnen"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:text-primary"
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {open && (
        <div className="fixed inset-x-0 top-16 z-40 border-b border-border bg-background">
          <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-6 md:px-12 py-6">
            {items.map((item) => {
              const accentHover = item.type === "group"
                ? item.basePath.startsWith("/wahlen-gewinnen")
                  ? "hover:text-brand-wahlen"
                  : item.basePath.startsWith("/politisch-kommunizieren")
                    ? "hover:text-brand-kommunikation"
                    : "hover:text-primary"
                : "hover:text-primary";
              const accentActive = item.type === "group"
                ? item.basePath.startsWith("/wahlen-gewinnen")
                  ? "text-brand-wahlen"
                  : item.basePath.startsWith("/politisch-kommunizieren")
                    ? "text-brand-kommunikation"
                    : "text-primary"
                : "text-primary";
              return item.type === "link" ? (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={`rounded-md px-3 py-3 font-display text-lg font-extrabold text-foreground transition-colors ${accentHover}`}
                  activeProps={{ className: accentActive }}
                >
                  {item.label}
                </Link>
              ) : (
                <div key={item.label} className="flex flex-col">
                  <button
                    type="button"
                    aria-expanded={!!openGroups[item.label]}
                    onClick={() => toggleGroup(item.label)}
                    className={`flex items-center justify-between rounded-md px-3 py-3 text-left font-display text-lg font-extrabold transition-colors ${accentHover} ${
                      isNavGroupActive(pathname, item) ? accentActive : "text-foreground"
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${openGroups[item.label] ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </button>
                  {openGroups[item.label] && (
                    <div className="ml-3 flex flex-col gap-1 border-l border-border pl-3">
                      {item.children.map((c) =>
                        c.external ? (
                          <a
                            key={c.to}
                            href={c.to}
                            target="_blank"
                            rel="noreferrer noopener"
                            onClick={() => setOpen(false)}
                            className={`rounded-md px-3 py-2 font-body text-base text-foreground transition-colors ${accentHover}`}
                          >
                            {c.label}
                          </a>
                        ) : (
                          <Link
                            key={c.to}
                            to={c.to}
                            onClick={() => setOpen(false)}
                            className={`rounded-md px-3 py-2 font-body text-base text-foreground transition-colors ${accentHover}`}
                            activeProps={{ className: accentActive }}
                          >
                            {c.label}
                          </Link>
                        ),
                      )}

                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/kontakt"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-md bg-brand-button px-3 py-3 text-center font-display text-lg font-extrabold text-brand-button-foreground"
            >
              Kontakt
            </Link>

            <div className="mt-4 flex items-center justify-center gap-6 border-t border-border pt-4">
              {socials.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </nav>
        </div>
      )}
    </div>
  );
}
